import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { RotateCcw, Warehouse, Radio, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTour } from '../../contexts/TourContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HelpToursTab() {
  const [companion, setCompanion] = useState(null);
  const { startTour } = useTour();

  useEffect(() => {
    axios.get(`${API_URL}/api/vending/companion/settings`)
      .then(r => setCompanion(r.data))
      .catch(() => setCompanion({ has_access: false }));
  }, []);

  // v2.5.888 — Replay Tour now BOTH resets progress and actually starts the tour
  // overlay via the shared TourContext. Previously we only redirected to
  // /dashboard, so users landed on the Platform Overview with no tour showing.
  const replayMainTour = async () => {
    try {
      await axios.post(`${API_URL}/api/tour/reset`);
      toast.success('Platform tour reset — starting the 14-step walkthrough…');
      // Land on /dashboard so the first spotlight target ("welcome") is on-page.
      if (window.location.pathname !== '/dashboard') {
        window.location.href = '/dashboard?tour=1';
      } else {
        startTour({ reset: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reset platform tour');
    }
  };

  const resetMiniTours = async () => {
    await axios.post(`${API_URL}/api/tour/mini/reset`);
    toast.success('Mini-tours reset! Visit any module to see the walkthrough again.');
  };

  // v2.5.897 — Vending replay: resilient path. Backend reset is best-effort;
  // if the deployed backend hasn't been updated with /tour/steps/vending/reset,
  // we still clear the session dismissal flag and hop to /vending so the tour
  // re-fires. The custom event lets us re-open the overlay without a full
  // reload when we're already on /vending.
  const replayVendingTour = async () => {
    try {
      await axios.post(`${API_URL}/api/tour/steps/vending/reset`);
    } catch (e) {
      // 404 or 500 — swallow. We can still replay the tour from the client side.
      console.warn('[Vending Tour] reset endpoint unavailable, replaying from client cache.');
    }
    try { sessionStorage.removeItem('vending_tour_dismissed'); } catch { /* storage may be blocked */ }
    toast.success('Opening the Vending Fleet tour…');
    if (window.location.pathname.startsWith('/vending')) {
      window.dispatchEvent(new CustomEvent('vending-tour-replay'));
    } else {
      setTimeout(() => { window.location.href = '/vending'; }, 400);
    }
  };

  const updateCompanion = async (patch) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/vending/companion/settings`, patch);
      setCompanion(prev => ({ ...prev, settings: data.settings }));
      toast.success('Companion settings updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update settings');
    }
  };

  // v2.5.888 — Vending replay button is ALWAYS visible for accounts that
  // can see the Vending module (super_admin, subscriber_admin, country_operator,
  // and eligible team_members). Reset is safe — it only clears the caller's own
  // progress. Companion-specific gating only applies to the Companion Settings
  // card below.
  const settings = companion?.settings || {};
  const canManageCompanion = companion?.has_access !== false && companion?.can_manage;

  return (
    <div className="space-y-4">
      <Card className="bg-white border-gray-border">
        <CardHeader><CardTitle className="text-lg font-heading">Guided Tours</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div>
              <p className="font-medium text-sm text-emerald-900">Platform Tour</p>
              <p className="text-xs text-emerald-700">14-step walkthrough of major platform features with AI voice narration — excludes Vending (module tour lives separately).</p>
            </div>
            <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={replayMainTour} data-testid="replay-main-tour">
              <RotateCcw className="h-3.5 w-3.5" />Replay Tour
            </Button>
          </div>
          <div className="flex items-center justify-between p-4 bg-teal-50 rounded-lg border border-teal-200">
            <div>
              <p className="font-medium text-sm text-teal-900">Module Mini-Tours</p>
              <p className="text-xs text-teal-700">3-step quick tips shown when you first visit each module</p>
            </div>
            <Button size="sm" variant="outline" className="gap-1.5 border-teal-300 text-teal-700" onClick={resetMiniTours} data-testid="reset-mini-tours">
              <RotateCcw className="h-3.5 w-3.5" />Reset Mini-Tours
            </Button>
          </div>
          <div className="flex items-center justify-between p-4 bg-sky-50 rounded-lg border border-sky-200" data-testid="vending-tour-card">
            <div>
              <p className="font-medium text-sm text-sky-900 flex items-center gap-1.5"><Warehouse className="h-4 w-4" /> Vending Fleet Tour</p>
              <p className="text-xs text-sky-700">10-step module-level walkthrough for the Vending Fleet — fires ONLY inside /vending. Separate from the Platform Tour above.</p>
            </div>
            <Button size="sm" className="gap-1.5 bg-sky-600 hover:bg-sky-700" onClick={replayVendingTour} data-testid="replay-vending-tour">
              <RotateCcw className="h-3.5 w-3.5" />Replay Vending Tour
            </Button>
          </div>
        </CardContent>
      </Card>

      {canManageCompanion && (
        <Card className="bg-white border-gray-border" data-testid="companion-settings-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2"><Radio className="h-4 w-4 text-sky-600" /> DEX Companion — Access &amp; Visibility</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-gray-medium">
              Control how the Konnect DEX Companion appears to your route/technical team. Both entry points remain fully functional independently — hide either to A/B test the flow.
            </p>
            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-border">
              <div>
                <Label htmlFor="companion-sidebar" className="text-sm font-medium">Sidebar link</Label>
                <p className="text-xs text-gray-medium">Show &quot;DEX Companion&quot; under the Vending Fleet section.</p>
              </div>
              <Switch
                id="companion-sidebar"
                checked={settings.sidebar_entry_enabled !== false}
                onCheckedChange={(v) => updateCompanion({ sidebar_entry_enabled: v })}
                data-testid="companion-sidebar-toggle"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-border">
              <div>
                <Label htmlFor="companion-standalone" className="text-sm font-medium">Standalone route</Label>
                <p className="text-xs text-gray-medium">Show the &quot;DEX Companion&quot; quick-action button on the Vending Fleet page header.</p>
              </div>
              <Switch
                id="companion-standalone"
                checked={settings.standalone_route_enabled !== false}
                onCheckedChange={(v) => updateCompanion({ standalone_route_enabled: v })}
                data-testid="companion-standalone-toggle"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-gray-border">
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="companion-allow-file">
                <input
                  type="checkbox"
                  checked={settings.allow_file_upload !== false}
                  onChange={(e) => updateCompanion({ allow_file_upload: e.target.checked })}
                />
                Enable File upload
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="companion-allow-paste">
                <input
                  type="checkbox"
                  checked={settings.allow_paste_dex !== false}
                  onChange={(e) => updateCompanion({ allow_paste_dex: e.target.checked })}
                />
                Enable Paste DEX
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="companion-allow-relay">
                <input
                  type="checkbox"
                  checked={settings.allow_relay !== false}
                  onChange={(e) => updateCompanion({ allow_relay: e.target.checked })}
                />
                Enable BLE/USB Relay
              </label>
            </div>
            <p className="text-[10px] text-gray-400 flex items-center gap-1 pt-1"><Settings2 className="h-3 w-3" /> Access is auto-granted to Platform Owners, activated Country Operators, Enterprise subscribers who own the Vending add-on, and users with a Route/Technical staff role.</p>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border-gray-border">
        <CardHeader><CardTitle className="text-lg font-heading">Support</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-gray-medium">Need help? Contact our support team or visit the knowledge base.</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => window.location.href = '/service/knowledge-base'}>Knowledge Base</Button>
            <Button size="sm" variant="outline" onClick={() => window.location.href = '/service/helpdesk'}>Submit a Ticket</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
